import { useCallback, useEffect, useMemo, useState } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { openLink } from '@tma.js/sdk-react';

import { env } from '@/env';
import { useGetLimuloGamesQuery } from '@/services/limuloApi';
import { useHaptics } from '@/hooks/useHaptics';
import { useStalled, useEverFailed, NO_ANSWER_MS } from '@/hooks/useStalled';
import { SectionCard } from '@/components/hub/SectionCard';
import { EmptyState } from '@/components/common/EmptyState';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { listVerdict } from '@/lib/listState';
import { crGameIdsOf } from '@/lib/limulo/code';
import { participantLabelOf } from '@/lib/limulo/match';
import { limuloMatchUrl } from '@/lib/limulo/routes';
import { partiteLive, type FamigliaMostrata, type PartitaLive } from '@/lib/limulo/partiteInCorso';
import { filtraLive, tuttiAccesi, vuotoPerFiltri, type FiltroLive } from '@/lib/limulo/filtriLive';
import { crWatchRedirectUrl } from '@/lib/crWatchLink';
import { cn } from '@/lib/cn';
import type { LimuloPartiteLive } from '@/types/limulo';
import { PartiteInCorsoDelGioco, type LetturaLive } from './live/CrLiveRaccoglitori';
import { SlotLiveHub } from '@/lib/limulo/slotLiveHub';
import { FiltriLive } from './live/FiltriLive';

/**
 * Hub Clash Royale · scheda «Live» sui tornei di Limulo — le partite CR in corso adesso, divise per
 * famiglia: code, sit&go, tornei (Luca, I-224), con tre filtri a scelta multipla e SENZA le sfide fra amici
 * (r11, call del 05/10: «le amichevoli non devono entrarci»). Le regole stanno in `lib/limulo/partiteInCorso.ts`
 * (le sfide) e `lib/limulo/filtriLive.ts` (i filtri); qui lo stato dei filtri, che e' del pannello.
 *
 * Le scelte di Luca (sportello, 2026-09-27 00:55Z):
 *  · L-2314 (a) — «in corso» = da «entrambi pronti» al risultato (`inCorso`, `lib/limulo/partiteInCorso.ts`);
 *  · L-2315 (a) — lotto 2: la lettura UNICA, `GET /v1/tournaments/live-matches` (@gionko kb#299,
 *    @phomea kb#300), una per gioco CR. Il lotto 1 leggeva a ventaglio (#459); e' cambiata la fonte,
 *    non la schermata;
 *  · L-2316 (b) — «Guarda» anche qui, col link spettatore del PROVIDER (ADR-0169 §9), che la riga
 *    porta in `spectatorLinks`. ⛔ Mai costruito dal tag (ADR-0157 §7). ⚠️ Il provider CR lo scrive e
 *    lo emette (`clashroyale://specHostedMatch?…`), ma motore e BFF lasciano passare solo http(s): la
 *    lista arriva vuota e il bottone non c'e' (kb#299/kb#300, 28/09). La riga intanto apre l'incontro.
 *
 * Disegno ripreso dalla Live backgammon (L-2298): la MIA partita in testa, una volta sola, con «Vai»;
 * le altre in sola lettura. Montata da `HubSections` solo con `VITE_FEATURE_CR_LIVE_LIMULO_ENABLED` e
 * Limulo accesi, e solo sotto Clash Royale: altrove resta `LiveMatchesPanel`.
 */

/**
 * Ogni quanto si ridisegna «da N min». 🔴 Senza, il numero resta quello del primo disegno: a dati
 * uguali il pannello non si ridisegna (`sameLettura`, e lo structural sharing di RTK tiene lo stesso
 * `data`) — revisione di #459, punto 2: «appena iniziata» restava li' per tutta la partita.
 */
export const OROLOGIO_LIVE_MS = 30_000;

function useOra(ogniMs: number): number {
  const [ora, setOra] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setOra(Date.now()), ogniMs);
    return () => window.clearInterval(id);
  }, [ogniMs]);
  return ora;
}

const TITOLO_FAMIGLIA: Readonly<Record<FamigliaMostrata, { key: string; defaultValue: string }>> = {
  coda: { key: 'live.family.coda', defaultValue: 'Queues' },
  'sit-and-go': { key: 'live.family.sitAndGo', defaultValue: 'Sit&Go' },
  torneo: { key: 'live.family.tournament', defaultValue: 'Tournaments' },
};

export function CrLiveLimuloPanel() {
  const { t } = useTranslation('hub');
  const limuloOn = env.featureLimuloEnabled && env.featureCrLiveLimuloEnabled;
  const ora = useOra(OROLOGIO_LIVE_MS);
  const VistaAlternativa = useContext(SlotLiveHub);

  // 1. Il catalogo: quali `gameId` dell'organizzazione sono Clash Royale (`profileType`), non un id
  //    scritto qui — il `gameId` e' dell'ORGANIZZAZIONE ("1" su staging), v. `crGameIdsOf`.
  const games = useGetLimuloGamesQuery(undefined, { skip: !limuloOn });
  const catalogoAnswered = games.data !== undefined;
  const catalogoFailed = useEverFailed(games.isError, catalogoAnswered);
  const crIds = useMemo(() => [...(crGameIdsOf(games.data) ?? [])].sort(), [games.data]);

  // 2. Gli incontri in corso di ciascun gioco CR: UNA lettura per gioco (lotto 2).
  const [letture, setLetture] = useState<Record<string, LetturaLive<LimuloPartiteLive>>>({});
  const onLettura = useCallback((gameId: string, l: LetturaLive<LimuloPartiteLive>) => {
    setLetture((prev) => (sameLettura(prev[gameId], l) ? prev : { ...prev, [gameId]: l }));
  }, []);

  const tutta = useMemo(
    () => partiteLive(crIds.flatMap((g) => letture[g]?.data?.partite ?? [])),
    [crIds, letture],
  );
  // I filtri sono del pannello: niente URL, niente storage. Tutti accesi all'apertura, e la Live parte
  // mostrando tutto. ⚠️ Il verdetto sotto si dice su `tutta`, NON su `live`: un filtro che toglie ogni riga
  // non e' «niente da guardare», e le righe e i filtri devono restare a schermo per poterli riaccendere.
  const [accesi, setAccesi] = useState<ReadonlySet<FiltroLive>>(tuttiAccesi);
  const live = useMemo(() => filtraLive(tutta, accesi), [tutta, accesi]);
  // Oltre la pagina letta: il motore le conta (`total`), la scheda dice quante non mostra.
  // `truncated` = il motore ha smesso di contare, e allora il numero e' un minimo.
  // ⚠️ Conta anche le partite delle famiglie spente da un filtro (e le sfide, che la Live non mostra mai):
  // il motore non le distingue e il server non filtra ancora per origine (kb#368, issuecomment-6003630061).
  // Finche' il filtro `origin` non e' lato server il numero e' quello della pagina, non quello dei filtri.
  const oltre = crIds.reduce((n, g) => {
    const d = letture[g]?.data;
    return n + (d ? Math.max(0, d.total - d.partite.length) : 0);
  }, 0);
  const troncata = crIds.some((g) => letture[g]?.data?.truncated === true);

  // ── Il verdetto: TRE fonti, ognuna coi suoi due segnali monotoni e le sue due soglie ──
  // (i cinque casi di un pannello senza righe: CLAUDE.md, «Gli 8 tab della Hub»)
  const catalogoPending = limuloOn && !catalogoAnswered && !catalogoFailed;
  const catalogoStalled = useStalled(catalogoPending);
  const catalogoNoAnswer = useStalled(catalogoPending, NO_ANSWER_MS);
  // 🔴 Un catalogo che ha risposto senza nessun gioco CR NON e' «nessuna partita»: e' il caso 4
  // («non posso chiedere»), come nella lobby backgammon. Da qui in giu' non si interroga niente.
  const crReachable = limuloOn && (!catalogoAnswered || crIds.length > 0);

  const liveAnswered = crIds.length > 0 && crIds.every((g) => letture[g]?.risposto === true);
  // 🔴 Basta UN gioco muto per non poter dire «vuoto»: un vuoto e' un'affermazione sull'INSIEME
  // (`listState.ts`). Con delle righe a schermo invece vincono le righe, che restano vere.
  const liveFailed = crIds.some(
    (g) => letture[g]?.fallito === true && letture[g]?.risposto !== true,
  );
  // ⚠️ In attesa anche mentre aspetta il CATALOGO: una fonte raggiungibile che non ha risposto e non
  // «carica» e' un errore per `listVerdict`, e la lettura non puo' partire prima del catalogo. E in
  // attesa anche mentre UN altro gioco CR non ha ancora risposto (revisione di #459, punto 3).
  const livePending = crReachable && !liveAnswered && !liveFailed;
  // ⚠️ Le due soglie invece contano solo da quando la richiesta PUO' partire, cioe' a catalogo arrivato:
  // un catalogo lento non deve consumare i 45 s della lettura (revisione di #459, punto 4).
  const liveInVolo = catalogoAnswered && livePending;
  const liveStalled = useStalled(liveInVolo);
  const liveNoAnswer = useStalled(liveInVolo, NO_ANSWER_MS);

  const verdict = listVerdict(tutta.totale, [
    {
      reachable: limuloOn,
      loading: games.isLoading,
      slow: catalogoStalled,
      failed: catalogoFailed || catalogoNoAnswer,
      answered: catalogoAnswered,
    },
    {
      reachable: crReachable,
      loading: livePending,
      slow: liveStalled,
      failed: liveFailed || liveNoAnswer,
      answered: liveAnswered,
    },
  ]);

  // I raccoglitori restano montati anche senza righe (v. `CrLiveRaccoglitori`) — e nello STESSO punto
  // dell'albero in entrambi i rami: smontati al passaggio fra «righe» e «nessuna riga» azzererebbero la
  // memoria del fallimento e il backoff (revisione di #459, punto 5).
  const raccoglitori = (
    <>
      {crIds.map((g) => (
        <PartiteInCorsoDelGioco key={g} gameId={g} onLettura={onLettura} />
      ))}
    </>
  );

  // La Live alternativa (fase 3, PR 5), se qualcuno sopra ne ha messa una nel Context: prende il posto dei SOLI due verdetti che hanno qualcosa da mostrare, `rows` e `empty`, con gli stessi dati e gli stessi raccoglitori. Va DOPO tutti gli hook: a Context vuoto il pannello e' quello di sempre.
  if (VistaAlternativa && (verdict === 'rows' || verdict === 'empty')) {
    return (
      <>
        {raccoglitori}
        <VistaAlternativa vuoto={verdict === 'empty'} live={tutta} ora={ora} oltre={oltre} troncata={troncata} gameIds={crIds} />
      </>
    );
  }

  if (verdict !== 'rows') {
    return (
      <>
        {raccoglitori}
        <div className="flex h-full flex-col">
          {(verdict === 'loading' || verdict === 'slow') && (
            <PanelWaiting slow={verdict === 'slow'} />
          )}
          {verdict === 'unavailable' && <PanelNotice tone="unavailable" />}
          {verdict === 'error' && <PanelNotice tone="error" />}
          {/* Il vuoto e' sull'INSIEME: la lettura unica chiede al motore TUTTI gli incontri in corso del
              gioco, non un campione di tornei. Il lotto 1 leggeva al piu' 20 tornei, e li' il vuoto
              doveva dichiarare il perimetro (revisione di #459, punto 1): con una fonte sola quel caso
              non esiste piu'. */}
          {verdict === 'empty' && (
            <EmptyState art="eyes">
              {t('live.empty', {
                defaultValue: 'Nothing to watch right now. Check back when a match is live.',
              })}
            </EmptyState>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {raccoglitori}
      <SectionCard flush className="flex min-h-0 flex-col overflow-hidden">
        <div
          data-testid="cr-live"
          className="flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain p-3 [touch-action:pan-y]"
        >
          <p className="px-1 text-sm font-semibold text-fg-muted">
            {t('live.header', { defaultValue: 'In progress now' })}{' '}
            <span className="text-fg">{live.totale}</span>
          </p>

          <FiltriLive accesi={accesi} onChange={setAccesi} />

          {live.mie.map((p) => (
            <RigaPartita
              key={`mia-${p.torneo.tournamentId}-${p.match.matchId}`}
              partita={p}
              ora={ora}
              mia
            />
          ))}

          {live.sezioni.map((s) => (
            <section
              key={s.famiglia}
              data-testid={`cr-live-${s.famiglia}`}
              className="flex flex-col gap-1.5"
            >
              <h3 className="px-1 pt-1 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
                {t(TITOLO_FAMIGLIA[s.famiglia].key, {
                  defaultValue: TITOLO_FAMIGLIA[s.famiglia].defaultValue,
                })}{' '}
                <span className="text-fg">{s.totale}</span>
              </h3>
              {s.gruppi.map((g) => (
                <div key={g.chiave} className="flex flex-col gap-1.5">
                  <p className="truncate px-1 text-xs font-semibold text-fg">{g.nome}</p>
                  {g.partite.map((p) => (
                    <RigaPartita
                      key={`${p.torneo.tournamentId}-${p.match.matchId}`}
                      partita={p}
                      ora={ora}
                    />
                  ))}
                </div>
              ))}
            </section>
          ))}

          {vuotoPerFiltri(tutta, live) && (
            <p data-testid="cr-live-vuoto-filtri" className="px-1 py-2 text-center text-xs text-fg-muted">
              {t('live.emptyFiltered', { defaultValue: 'No matches with these filters.' })}
            </p>
          )}

          {oltre > 0 && (
            <p data-testid="cr-live-oltre" className="pt-1 text-center text-[11px] text-fg-muted">
              {troncata
                ? t('live.cappedMatchesAtLeast', {
                    count: oltre,
                    defaultValue: 'At least {{count}} more matches in progress not shown',
                  })
                : t('live.cappedMatches', {
                    count: oltre,
                    defaultValue: '+{{count}} more matches in progress not shown',
                  })}
            </p>
          )}
        </div>
      </SectionCard>
    </>
  );
}

/** Stessi valori ⇒ stesso oggetto: un raccoglitore che riporta a ogni render non deve ciclare. */
function sameLettura<T>(a: LetturaLive<T> | undefined, b: LetturaLive<T>): boolean {
  return (
    a !== undefined && a.data === b.data && a.risposto === b.risposto && a.fallito === b.fallito
  );
}

function RigaPartita({
  partita,
  ora,
  mia = false,
}: {
  partita: PartitaLive;
  ora: number;
  mia?: boolean;
}) {
  const { t } = useTranslation('hub');
  const { t: tl } = useTranslation('limulo');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const { match, torneo, da, guarda } = partita;
  const a = participantLabelOf(match.players.a, tl);
  const b = participantLabelOf(match.players.b, tl);
  const minuti = da === null ? null : Math.max(0, Math.floor((ora - da) / 60_000));
  const apri = () => {
    haptics.impact('light');
    navigate(limuloMatchUrl(torneo.tournamentId, match.matchId));
  };
  const riga = (
    <button
      type="button"
      onClick={apri}
      data-testid={mia ? 'cr-live-mia' : 'cr-live-riga'}
      className={cn(
        'card-edge flex w-full items-center justify-between gap-2 rounded-xl bg-bg-secondary p-2.5 text-left transition-opacity active:opacity-80',
        mia && 'border border-brand',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-fg">
          <span className={cn(a.smorzato && 'text-fg-muted')}>{a.testo}</span>{' '}
          <span className="text-fg-muted">{t('live.vs', { defaultValue: 'vs' })}</span>{' '}
          <span className={cn(b.smorzato && 'text-fg-muted')}>{b.testo}</span>
        </span>
        <span className="block truncate text-[10px] text-fg-muted">
          {mia ? torneo.name || torneo.tournamentId : null}
          {mia && minuti !== null ? ' · ' : null}
          {minuti === null
            ? null
            : minuti === 0
              ? t('live.justStarted', { defaultValue: 'just started' })
              : t('live.since', { count: minuti, defaultValue: 'for {{count}} min' })}
        </span>
      </span>
      {mia && (
        <span className="shrink-0 rounded-full bg-brand px-3 py-1 text-[11px] font-bold text-brand-fg">
          {t('live.go', { defaultValue: 'Go' })}
        </span>
      )}
    </button>
  );
  // «Guarda» (L-2316 b) solo sulle partite degli ALTRI e solo col link del provider. Accanto alla riga,
  // non dentro: un bottone dentro un bottone non e' HTML valido, e il tocco sulla riga apre l'incontro.
  // ⚠️ `openLink` e non `<a href>`: la WebView di Telegram tiene i link dentro di se'. Il link del gioco
  // (`clashroyale:`) la WebView lo blocca del tutto, quindi passa dalla pagina ponte (`crWatchLink.ts`).
  if (mia || guarda === null) return riga;
  const guardaOra = () => {
    haptics.impact('light');
    openLink.ifAvailable(guarda.tipo === 'app' ? crWatchRedirectUrl(guarda.url) : guarda.url);
  };
  return (
    <div className="flex items-stretch gap-1.5">
      <div className="min-w-0 flex-1">{riga}</div>
      <button
        type="button"
        onClick={guardaOra}
        data-testid="cr-live-guarda"
        className="card-edge shrink-0 rounded-xl bg-bg-secondary px-3 text-[11px] font-bold text-brand transition-opacity active:opacity-80"
      >
        {t('live.watch', { defaultValue: 'Watch' })}
      </button>
    </div>
  );
}
